import React, { useState } from "react";
import { Link } from "react-router-dom";
import { formatDate } from "@/utils/timezone";
import api from "@/api";

/**
 * IntelSidebar - Slide-in panel showing intel items for a specific ticker
 */
const IntelSidebar = ({ isOpen, onClose, ticker, intel, accountId, onIntelDeleted }) => {
  const [viewingImage, setViewingImage] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Handle escape key to close sidebar or image viewer
  React.useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === "Escape") {
        if (viewingImage) {
          setViewingImage(null);
        } else {
          onClose();
        }
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose, viewingImage]);

  const handleDeleteIntel = async (intelId) => {
    if (!window.confirm("Are you sure you want to delete this intel item?")) {
      return;
    }

    try {
      setDeletingId(intelId);
      await api.delete(`/intel/${intelId}`);

      // Notify parent to refresh intel list
      if (onIntelDeleted) {
        onIntelDeleted(intelId);
      }
    } catch (error) {
      console.error("Error deleting intel:", error);
      alert("Failed to delete intel");
    } finally {
      setDeletingId(null);
    }
  };

  if (!isOpen) return null;

  // Filter intel by ticker
  const tickerIntel = intel.filter((item) =>
    item.tickers && item.tickers.includes(ticker)
  );

  // Sort by timestamp (newest first)
  const sortedIntel = [...tickerIntel].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.5)",
          zIndex: 1000,
          animation: "fadeIn 0.2s ease-out",
        }}
      />

      {/* Sidebar */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "500px",
          maxWidth: "90vw",
          background: "var(--card-bg)",
          boxShadow: "-4px 0 16px rgba(0, 0, 0, 0.2)",
          zIndex: 1001,
          display: "flex",
          flexDirection: "column",
          animation: "slideInRight 0.3s ease-out",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "1.5rem",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h2 style={{ fontSize: "1.5rem", fontWeight: "700", marginBottom: "0.25rem" }}>
              {ticker} Intel
            </h2>
            <p style={{ fontSize: "0.875rem", color: "#718096" }}>
              {sortedIntel.length} item{sortedIntel.length !== 1 ? "s" : ""}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              fontSize: "1.5rem",
              cursor: "pointer",
              color: "#718096",
              padding: "0.25rem",
              lineHeight: 1,
            }}
            title="Close"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "1.5rem",
          }}
        >
          {sortedIntel.length === 0 ? (
            <div style={{ textAlign: "center", padding: "2rem", color: "#718096" }}>
              No intel found for {ticker}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
              {sortedIntel.map((item, index) => (
                <div
                  key={item.id || index}
                  style={{
                    background: "#f7fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    padding: "1rem",
                    position: "relative",
                  }}
                >
                  {/* Delete button */}
                  <button
                    onClick={() => handleDeleteIntel(item.id)}
                    disabled={deletingId === item.id}
                    style={{
                      position: "absolute",
                      top: "0.5rem",
                      right: "0.5rem",
                      background: deletingId === item.id ? "#cbd5e0" : "#fed7d7",
                      border: "1px solid #fc8181",
                      borderRadius: "4px",
                      color: "#c53030",
                      fontSize: "0.75rem",
                      padding: "0.25rem 0.5rem",
                      cursor: deletingId === item.id ? "not-allowed" : "pointer",
                      fontWeight: "600",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      if (deletingId !== item.id) {
                        e.currentTarget.style.background = "#fc8181";
                        e.currentTarget.style.color = "white";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (deletingId !== item.id) {
                        e.currentTarget.style.background = "#fed7d7";
                        e.currentTarget.style.color = "#c53030";
                      }
                    }}
                    title="Delete this intel"
                  >
                    {deletingId === item.id ? "Deleting..." : "Delete"}
                  </button>

                  {/* Screenshot */}
                  {item.screenshot_data && (
                    <div
                      style={{
                        marginBottom: "1rem",
                        borderRadius: "4px",
                        overflow: "hidden",
                        border: "1px solid #e2e8f0",
                        cursor: "pointer",
                      }}
                      onClick={() => setViewingImage(item.screenshot_data)}
                      title="Click to view full size"
                    >
                      <img
                        src={item.screenshot_data}
                        alt="Intel screenshot"
                        style={{
                          width: "100%",
                          height: "auto",
                          display: "block",
                        }}
                      />
                    </div>
                  )}

                  {/* Metadata */}
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
                    {item.tickers && item.tickers.map((t) => (
                      <Link
                        key={t}
                        to={`/ticker/${t}${accountId ? `?accountId=${accountId}` : ''}`}
                        style={{ textDecoration: 'none' }}
                      >
                        <span
                          style={{
                            background: t === ticker ? "#4299e1" : "#718096",
                            color: "white",
                            padding: "0.125rem 0.5rem",
                            borderRadius: "4px",
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            cursor: "pointer",
                          }}
                        >
                          {t}
                        </span>
                      </Link>
                    ))}
                    {item.platform && (
                      <span style={{ fontSize: "0.75rem", color: "#718096" }}>
                        via {item.platform}
                      </span>
                    )}
                    {item.author && (
                      <span style={{ fontSize: "0.75rem", color: "#4a5568", fontWeight: "600" }}>
                        {item.author}
                      </span>
                    )}
                  </div>

                  {/* Thesis */}
                  {item.thesis && (
                    <div
                      style={{
                        fontSize: "0.875rem",
                        color: "#2d3748",
                        marginBottom: "0.75rem",
                        lineHeight: "1.5",
                      }}
                    >
                      {item.thesis}
                    </div>
                  )}

                  {/* Tags */}
                  {item.tags && item.tags.length > 0 && (
                    <div style={{ display: "flex", gap: "0.25rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
                      {item.tags.map((tag) => (
                        <span
                          key={tag}
                          style={{
                            background: "#e2e8f0",
                            color: "#4a5568",
                            padding: "0.125rem 0.375rem",
                            borderRadius: "4px",
                            fontSize: "0.625rem",
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Timestamp */}
                  <div style={{ fontSize: "0.7rem", color: "#a0aec0" }}>
                    {formatDate(item.timestamp, "MMM d, yyyy h:mm a")}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Image Viewer Modal */}
      {viewingImage && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setViewingImage(null)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.9)",
              zIndex: 2000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              animation: "fadeIn 0.2s ease-out",
            }}
          >
            {/* Close button */}
            <button
              onClick={() => setViewingImage(null)}
              style={{
                position: "absolute",
                top: "1rem",
                right: "1rem",
                background: "rgba(255, 255, 255, 0.2)",
                border: "none",
                color: "white",
                fontSize: "2rem",
                width: "3rem",
                height: "3rem",
                borderRadius: "50%",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
              }}
              title="Close (ESC)"
            >
              ✕
            </button>

            {/* Full-size Image */}
            <img
              src={viewingImage}
              alt="Full size screenshot"
              style={{
                maxWidth: "95vw",
                maxHeight: "95vh",
                objectFit: "contain",
                borderRadius: "4px",
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </>
      )}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
      `}</style>
    </>
  );
};

export default IntelSidebar;
